import { mapApiToAppointment } from 'core/appointments/infra/appointmentMapper'
import { mapApiToContainer } from 'core/container-details/infra/ContainerMapper'
import { type Shipper } from 'core/domain/Shipper'
import { mapApiToManagedBy } from 'core/infra/ManagedByMapper.ts'
import {
  mapApiToShippingItems,
  mapApiToShippingMeasures,
} from 'core/shipping-items/infra/ShippingItemsMapper'
import { createDate } from 'core/utils/date'

import {
  type Accessorial,
  type EquipmentRequirements,
  type RawEquipmentRequirements,
  type RawTenderAttachment,
  type RawTenderCarrier,
  type RawTenderOffer,
  type RawTenderOfferListResponse,
  type RawTenderShipper,
  type RawTenderStop,
  type TenderAttachment,
  type TenderCarrier,
  type TenderOffer,
  type TenderRate,
  type TenderStop,
} from '../domain/TenderOffer'

function mapApiToStop(rawStop: RawTenderStop): TenderStop {
  const appointment = mapApiToAppointment(rawStop.appointment)
  return {
    address: rawStop.address || undefined,
    city: rawStop.city,
    country: rawStop.country || undefined,
    date: rawStop.date || undefined,
    latitude: rawStop.latitude || undefined,
    longitude: rawStop.longitude || undefined,
    state: rawStop.state,
    stopIndex: rawStop.stop_index,
    zipCode: rawStop.zipcode || undefined,
    stopType: rawStop.stop_type,
    appointment,
  }
}

function mapApiToShipper(rawShipper: RawTenderShipper): Shipper {
  return {
    companyName: rawShipper.company_name,
    email: rawShipper.email,
    logoUrl: rawShipper.logo_email_file || undefined,
    name: rawShipper.name || undefined,
    phoneNumber: rawShipper.phone_number || undefined,
  }
}

function mapApiToCarrier(rawCarrier: RawTenderCarrier): TenderCarrier {
  return {
    id: rawCarrier.id,
    dot: rawCarrier.dot ?? undefined,
    mc: rawCarrier.mc ?? undefined,
    name: rawCarrier.name,
    email: rawCarrier.email,
    entityType: rawCarrier.entity_type ?? undefined,
  }
}

function mapApiToAttachment(rawAttachment: RawTenderAttachment): TenderAttachment {
  return {
    id: rawAttachment.id,
    fileName: rawAttachment.file_name,
    url: rawAttachment.url,
  }
}

function mapApiToEquipmentRequirements(
  equipmentRequirements?: RawEquipmentRequirements | null
): EquipmentRequirements | undefined {
  if (!equipmentRequirements) {
    return undefined
  }

  return {
    temperature: equipmentRequirements.temperature || undefined,
    temperatureUnit: equipmentRequirements.unit || undefined,
  }
}

function mapApiToEquipmentSubtypes(equipmentRequirements?: RawEquipmentRequirements | null) {
  return equipmentRequirements?.equipment_subtypes || undefined
}

function mapApiToRate(rawRate: RawTenderOffer['rate']): TenderRate {
  return {
    uuid: rawRate.uuid ?? undefined,
    providerRateId: rawRate.provider_rate_id ?? undefined,
    contractedLane: rawRate.contracted_lane
      ? {
          id: rawRate.contracted_lane.id,
          contract: {
            id: rawRate.contracted_lane.contract.id,
            title: rawRate.contracted_lane.contract.title,
          },
          carrierInviteUUID: rawRate.contracted_lane.carrier_invite_uuid ?? undefined,
        }
      : undefined,
  }
}

function mapApiToAccessorial(rawAccessorial: Accessorial) {
  return {
    name: rawAccessorial.name,
    code: rawAccessorial.code,
  }
}

export function mapApiToTenderOffer(raw: RawTenderOffer): TenderOffer {
  const flatRate = parseFloat(raw.flat_rate)
  const pricePerMile = raw.mileage > 0 ? flatRate / raw.mileage : 0

  return {
    id: raw.uuid,
    accessorials: raw.accessorials?.map(mapApiToAccessorial) ?? [],
    shipper: mapApiToShipper(raw.shipper),
    carrier: mapApiToCarrier(raw.carrier),
    mode: raw.mode,
    rateType: raw.rate_type,
    expirationDate: createDate(raw.reply_deadline),
    shipperGuideId: raw.shipper_guide_id,
    stops: raw.stops.map(mapApiToStop),
    equipmentType: raw.equipment_type.value,
    equipmentTypeLabel: raw.equipment_type.label,
    mileage: raw.mileage,
    flatRate,
    pricePerMile,
    status: raw.status,
    notes: raw.notes || undefined,
    items: mapApiToShippingItems(raw.items),
    createdAt: createDate(raw.created_at),
    shareDetails: raw.share_details,
    measures: mapApiToShippingMeasures(raw.measures),
    powerOnly: raw.power_only,
    attachments: raw.attachments.map(mapApiToAttachment),
    carrier1xpLink: raw.carrier_1xp_link || undefined,
    managedBy: mapApiToManagedBy(raw.managed_by),
    rate: mapApiToRate(raw.rate),
    equipmentRequirements: mapApiToEquipmentRequirements(raw.equipment_requirements),
    equipmentSubtypes: mapApiToEquipmentSubtypes(raw.equipment_requirements),
    quoteUUID: raw.quote_uuid ?? undefined,
    container: mapApiToContainer(raw.container),
  }
}

export function mapApiToTenderOfferList(response: RawTenderOfferListResponse): Array<TenderOffer> {
  return response.results.map(mapApiToTenderOffer)
}
